import { useQuery } from '@tanstack/react-query';
import { BookOpen, FlaskConical, GraduationCap, ListChecks } from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/ui/Alert';
import { Badge, ProgressBadge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/Progress';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { fetchers, queryKeys } from '@/services/queries';

export function MyCoursesPage() {
  const progress = useQuery({ queryKey: queryKeys.progress, queryFn: fetchers.progress });

  if (progress.isLoading) return <FullPageSpinner label="Chargement de vos formations…" />;

  const tracks = progress.data ?? [];

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Mes formations</h1>
        <p className="mt-1 text-sm text-muted">
          Les parcours auxquels vous êtes inscrit, avec votre progression détaillée par module.
        </p>
      </header>

      {tracks.length === 0 ? (
        <EmptyState
          icon={<GraduationCap className="mx-auto h-8 w-8" />}
          title="Aucune formation accessible"
          description="Un administrateur doit vous inscrire à un parcours pour que les modules apparaissent ici."
          action={
            <ButtonLink to="/parcours" variant="outline">
              Consulter le catalogue
            </ButtonLink>
          }
        />
      ) : (
        tracks.map((track) => (
          <Card key={track.track_id}>
            <CardHeader className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle as="h2">{track.track_title}</CardTitle>
                <p className="mt-0.5 text-xs text-faint">
                  {track.hours_total} heures · {track.modules_total} modules ouverts
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  tone={
                    track.track_code === 'A' ? 'tech' : track.track_code === 'B' ? 'ai' : 'agent'
                  }
                >
                  Parcours {track.track_code}
                </Badge>
                <Link
                  to={`/parcours/${track.track_slug}`}
                  className="text-xs font-semibold text-tech hover:underline"
                >
                  Programme
                </Link>
              </div>
            </CardHeader>
            <CardBody>
              <ProgressBar value={track.percent} label="Progression du parcours" />

              {track.modules.length === 0 ? (
                <p className="mt-5 text-sm text-muted">
                  Aucun module de ce parcours n'est encore ouvert. Les modules sont publiés
                  progressivement.
                </p>
              ) : (
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  {track.modules.map((module) => (
                    <Card key={module.module_id} className="bg-surface-2/40 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-mono text-xs font-bold text-tech">
                            {module.module_code}
                          </p>
                          <h3 className="mt-1 text-sm font-semibold text-fg">
                            {module.module_title}
                          </h3>
                        </div>
                        <ProgressBadge status={module.status} />
                      </div>

                      <ProgressBar value={module.percent} className="mt-3" size="sm" />

                      <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded border border-border bg-surface px-2 py-2">
                          <BookOpen className="mx-auto h-3.5 w-3.5 text-faint" aria-hidden="true" />
                          <dd className="mt-1 text-xs font-bold tabular-nums text-fg">
                            {module.chapters_completed}/{module.chapters_total}
                          </dd>
                          <dt className="text-2xs text-faint">chapitres</dt>
                        </div>
                        <div className="rounded border border-border bg-surface px-2 py-2">
                          <FlaskConical
                            className="mx-auto h-3.5 w-3.5 text-faint"
                            aria-hidden="true"
                          />
                          <dd className="mt-1 text-xs font-bold tabular-nums text-fg">
                            {module.labs_completed}/{module.labs_total}
                          </dd>
                          <dt className="text-2xs text-faint">TP</dt>
                        </div>
                        <div className="rounded border border-border bg-surface px-2 py-2">
                          <ListChecks className="mx-auto h-3.5 w-3.5 text-faint" aria-hidden="true" />
                          <dd className="mt-1 text-xs font-bold tabular-nums text-fg">
                            {module.quizzes_passed}/{module.quizzes_total}
                          </dd>
                          <dt className="text-2xs text-faint">quiz</dt>
                        </div>
                      </dl>

                      <Link
                        to={`/modules/${module.module_code}`}
                        className="mt-4 block text-center text-xs font-semibold text-tech hover:underline"
                      >
                        Ouvrir le module
                      </Link>
                    </Card>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        ))
      )}
    </div>
  );
}
